import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

gsap.registerPlugin(ScrollTrigger, SplitText)

const ComingSoon = {
  instances: new Map(),
  marqueeSpeed: 30,

  init() {
    document.querySelectorAll('[data-coming-soon]').forEach(component => {
      if (this.instances.has(component) || component.closest('.editor-styles-wrapper')) return

      const media = gsap.matchMedia()
      this.instances.set(component, media)

      media.add('(prefers-reduced-motion: no-preference)', () => {
        const videoPlayback = Array.from(component.querySelectorAll('[data-coming-soon-video]'), video => {
          const animation = {
            paused(paused) {
              if (paused) {
                video.pause()
              } else {
                video.play()?.catch(() => {})
              }
            }
          }
          const playback = this.observePlayback(video, () => [animation])

          return () => {
            playback.destroy()
            video.pause()
          }
        })
        const cleanupMarquee = this.createMarquee(component)
        const cleanupClients = this.createClientsMarquee(component)
        const cleanupFooter = this.createFooterWordmark(component)
        const cleanupEntrance = this.createEntrance(component)
        const rings = component.querySelectorAll('[data-coming-soon-ring]')

        const ringAnimations = Array.from(rings, (ring, index) => {
          return gsap
            .timeline({ repeat: -1 })
            .fromTo(ring, { scale: 0.25 }, { scale: 1.3, duration: 12, ease: 'none' }, 0)
            .fromTo(ring, { opacity: 0 }, { opacity: 1, duration: 2, ease: 'sine.out' }, 0)
            .to(ring, { opacity: 0, duration: 8, ease: 'sine.in' }, 4)
            .progress(index / rings.length)
        })

        const ringPlayback = rings.length ? this.observePlayback(rings[0].parentElement, () => ringAnimations) : null

        return () => {
          videoPlayback.forEach(cleanup => cleanup())
          ringPlayback?.destroy()
          cleanupMarquee?.()
          cleanupClients?.()
          cleanupFooter?.()
          cleanupEntrance()
        }
      })
    })
  },

  createEntrance(component) {
    const cleanups = Array.from(component.querySelectorAll('[data-coming-soon-reveal-group]'), group => {
      const elements = Array.from(group.querySelectorAll('[data-coming-soon-reveal]'))
      const text = elements.filter(element => element.dataset.comingSoonReveal === 'lines')
      let timeline
      let started = false
      let completed = false

      const animate = split => {
        // A completed entrance stays visible when fonts load or lines reflow.
        if (completed) {
          timeline = gsap.timeline()
          return timeline
        }

        timeline = gsap.timeline({
          paused: !started,
          defaults: { duration: 1.5, ease: 'power4.out', lazy: false },
          delay: 0.3,
          onComplete: () => {
            completed = true
          }
        })
        let position = 0

        elements.forEach(element => {
          const lines = split?.lines.filter(line => element.contains(line)) || []
          const targets = lines.length ? lines : [element]
          timeline.from(targets, { opacity: 0, y: 18, stagger: 0.03 }, position)
          position += targets.length * 0.03 + 0.08
        })

        return timeline
      }

      const split = text.length ? SplitText.create(text, { type: 'lines', autoSplit: true, onSplit: animate }) : null
      if (!split) animate()

      const trigger = ScrollTrigger.create({
        trigger: group,
        start: 'top 95%',
        once: true,
        onEnter: () => {
          started = true
          timeline.play()
        }
      })
      const revealFocused = () => {
        started = true
        timeline.progress(1)
      }
      group.addEventListener('focusin', revealFocused)

      return () => {
        group.removeEventListener('focusin', revealFocused)
        trigger.kill()
        timeline.revert()
        split?.revert()
      }
    })

    return () => cleanups.forEach(cleanup => cleanup())
  },

  observePlayback(element, getAnimations) {
    let visible = !('IntersectionObserver' in window)
    const sync = () => getAnimations().forEach(animation => animation?.paused(!visible || document.hidden))
    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver(entries => {
            visible = entries[0].isIntersecting
            sync()
          })
        : null

    observer?.observe(element)
    document.addEventListener('visibilitychange', sync)
    sync()

    return {
      sync,
      destroy() {
        observer?.disconnect()
        document.removeEventListener('visibilitychange', sync)
      }
    }
  },

  createFooterWordmark(component) {
    const footer = component.querySelector('[data-coming-soon-footer]')
    const letters = footer?.querySelectorAll('[data-coming-soon-footer-letter]')
    if (!letters?.length) return

    const animation = gsap.fromTo(
      letters,
      { yPercent: 100, opacity: 0 },
      {
        yPercent: 0,
        opacity: 1,
        duration: 1.2,
        stagger: 0.045,
        ease: 'none',
        scrollTrigger: {
          trigger: footer,
          start: 'top 80%',
          end: 'bottom bottom',
          scrub: 2
        }
      }
    )

    return () => {
      animation.scrollTrigger.kill()
      animation.revert()
    }
  },

  createMarquee(component) {
    const container = component.querySelector('[data-coming-soon-photos]')
    const photos = Array.from(component.querySelectorAll('[data-coming-soon-photo]'))
    if (!container || !photos.length) return

    let context
    let timeline
    let width
    const copies = []
    const videos = []
    const playback = this.observePlayback(container, () => [timeline, ...videos])
    const removeCopies = () => {
      videos.forEach(video => video.paused(true))
      videos.length = 0
      copies.forEach(copy => copy.remove())
      copies.length = 0
    }

    const rebuild = () => {
      if (container.clientWidth === width) return
      width = container.clientWidth
      const progress = timeline ? timeline.progress() : 0
      context?.revert()
      removeCopies()
      if (!width) return

      const positions = photos.map(photo => ({ left: photo.offsetLeft, width: photo.offsetWidth }))
      const sorted = [...positions].sort((first, second) => first.left - second.left)
      const maxWidth = Math.max(...positions.map(position => position.width))
      const start = sorted[0].left
      const end = Math.max(...positions.map(position => position.left + position.width))
      const gap =
        sorted.length > 1
          ? sorted.slice(1).reduce((total, position, index) => total + Math.max(0, position.left - sorted[index].left - sorted[index].width), 0) /
            (sorted.length - 1)
          : maxWidth
      const groupWidth = end - start + gap
      const repeatCount = Math.ceil((width + maxWidth) / groupWidth)
      const distance = groupWidth * repeatCount

      // Repeat the staggered pattern at its natural spacing, including on wide screens.
      for (let repeat = 1; repeat < repeatCount; repeat += 1) {
        photos.forEach(photo => {
          const copy = photo.cloneNode(true)
          copy.removeAttribute('data-coming-soon-photo')
          copy.removeAttribute('data-coming-soon-video')
          copy.setAttribute('aria-hidden', 'true')
          copy.setAttribute('inert', '')
          container.append(copy)
          copies.push(copy)

          if (copy.tagName === 'VIDEO') {
            copy.muted = true
            videos.push({
              paused(paused) {
                if (paused) {
                  copy.pause()
                } else {
                  copy.play()?.catch(() => {})
                }
              }
            })
          }
        })
      }

      context = gsap.context(() => {
        timeline = gsap.timeline({ repeat: -1 })
        const items = [...photos, ...copies]
        items.forEach((photo, index) => {
          const position = positions[index % photos.length]
          const offset = Math.floor(index / photos.length) * groupWidth
          const min = -position.width - position.left
          const wrap = gsap.utils.wrap(min, min + distance)
          timeline.fromTo(
            photo,
            { x: wrap(offset) },
            {
              x: wrap(offset) - distance,
              duration: distance / this.marqueeSpeed,
              ease: 'none',
              modifiers: { x: value => `${wrap(parseFloat(value))}px` }
            },
            0
          )
        })
        timeline.progress(progress)
        playback.sync()
      }, component)
    }

    rebuild()
    const observer = new ResizeObserver(rebuild)
    observer.observe(container)

    return () => {
      observer.disconnect()
      playback.destroy()
      context?.revert()
      removeCopies()
    }
  },

  createClientsMarquee(component) {
    const viewport = component.querySelector('[data-coming-soon-clients]')
    const gallery = viewport?.querySelector('[data-coming-soon-clients-gallery]')
    if (!gallery?.children.length) return

    let tween
    let previousSize = ''
    const copies = []
    const playback = this.observePlayback(viewport, () => [tween])
    viewport.classList.add('is-active')

    const rebuild = () => {
      const gap = parseFloat(getComputedStyle(gallery).columnGap) || 0
      const distance = gallery.getBoundingClientRect().width + gap
      const size = `${viewport.clientWidth}:${distance}`
      if (size === previousSize) return
      previousSize = size
      const progress = tween?.progress() || 0
      tween?.revert()
      copies.forEach(copy => copy.remove())
      copies.length = 0
      if (!viewport.clientWidth || !distance) return

      // Repeat enough logos to cover the viewport throughout the entire loop.
      for (let index = 0; index < Math.ceil(viewport.clientWidth / distance); index += 1) {
        const copy = gallery.cloneNode(true)
        copy.removeAttribute('data-coming-soon-clients-gallery')
        copy.setAttribute('aria-hidden', 'true')
        copy.setAttribute('inert', '')
        viewport.append(copy)
        copies.push(copy)
      }

      tween = gsap.fromTo([gallery, ...copies], { x: 0 }, { x: -distance, duration: distance / this.marqueeSpeed, ease: 'none', repeat: -1 })
      tween.progress(progress)
      playback.sync()
    }

    rebuild()
    const observer = new ResizeObserver(rebuild)
    observer.observe(viewport)
    observer.observe(gallery)

    return () => {
      observer.disconnect()
      playback.destroy()
      tween?.revert()
      copies.forEach(copy => copy.remove())
      viewport.classList.remove('is-active')
    }
  },

  destroy() {
    this.instances.forEach(media => media.revert())
    this.instances.clear()
  }
}

export default ComingSoon
